import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { Unity, useUnityContext } from 'react-unity-webgl';
import { useScript } from '../../../utils/hooks';
import { useAdsListener } from '../../../services/admob';
import { AuthContext } from '../../../context/AuthContext';

// Unity Context 생성
const UnityGameContext = createContext();

const PREFIX_URL = '/game-egg';

// Unity Provider 컴포넌트
export const UnityGameProviderEgg = ({ children }) => {
  useScript(PREFIX_URL + `/JS/YaongEgg.js`);
  const { addLoginListener } = useContext(AuthContext);

  const [mode, setMode] = useState(''); // 게임 mode 상태
  const [showInModal, setShowInModal] = useState(false); // 내부에서 모달 핸들링

  const unityContextValue = useUnityContext({
    companyName: 'YaongEgg',
    loaderUrl: PREFIX_URL + '/Build/Build.loader.js',
    dataUrl: PREFIX_URL + '/Build/Build.data',
    frameworkUrl: PREFIX_URL + '/Build/Build.framework.js',
    codeUrl: PREFIX_URL + '/Build/Build.wasm',
    devicePixelRatio: 1.5,
  });

  const { unityProvider, sendMessage, loadingProgression, isLoaded: unityIsLoaded } = unityContextValue;

  const [isFullyReady, setIsFullyReady] = useState(false); // Unity 완전 준비 상태
  const pendingCallsRef = useRef([]); // 대기 중인 함수 호출들
  const readyTimeoutRef = useRef(null);
  const adCallbackRef = useRef([]); // 광고 콜백 대기 함수

  const handleLoginSuccess = (token) => {
    // console.log('Login successful:', token); // for debugging ::: { "accessToken": "eyJ0eXB...lIjoiand0IiwiYWxnIjoiSFM1MTIifQ.eyJ1c2VyTm8iOjI1LCJpYXQiOjE3NTAxNjc1OTAsImV4cCI6MTc1MDc3MjM5MH0.yE2TLioviBsKWTrfXiIqKm5NZHJ_lS3qdtd0I0eQHQWL3tALx9qWlGjYMTEaLwneM3oshcWT2V691YctYIT-6w"}
    fnSendMessage('WebInterfaceMgr', 'OnModeResult', 'login'); // 초기 화면
  };

  // 게임 mode 가져오기
  const setGameMode = (mode) => {
    setMode(mode);
  };

  // 안전한 Unity 함수 호출
  const fnSendMessage = useCallback(
    (goName, fnResult, arg) => {
      if (isFullyReady) {
        // Unity가 완전히 준비되어 있으면 즉시 호출
        try {
          // console.log('Sending message immediately:', goName, fnResult, arg); // 디버깅용 로그
          sendMessage(goName, fnResult, arg);
        } catch (error) {
          // console.error('Unity sendMessage failed:', error); // 디버깅용 로그
        }
      } else {
        // Unity가 아직 준비되지 않았으면 대기열에 추가
        // console.log('Unity not ready, queuing message:', goName, fnResult, arg);
        pendingCallsRef.current.push({ goName, fnResult, arg });
      }
    },
    [isFullyReady, sendMessage],
  );

  // 대기 중인 함수 호출들 처리
  const processPendingCalls = useCallback(() => {
    if (pendingCallsRef.current.length > 0 && isFullyReady) {
      pendingCallsRef.current.forEach(({ goName, fnResult, arg }) => {
        try {
          sendMessage(goName, fnResult, arg);
          // console.log(`Unity call executed: ${goName}.${fnResult}(${arg})`); // 디버깅용 로그
        } catch (error) {
          // console.error(`Unity call failed: ${goName}.${fnResult}`, error); // 디버깅용 로그
        }
      });

      // 호출 완료 후 대기 목록 초기화
      pendingCallsRef.current = [];
    }
  }, [isFullyReady, sendMessage]);

  //  3) ad - 광고 시청 후 콜백 핸들러
  async function checkAdsEgg() {
    adCallbackRef.current.forEach(({ goName, fnResult, arg }) => {
      try {
        sendMessage(goName, fnResult, arg);
      } catch (error) {
        console.error(`checkAdsEgg failed: ${goName}.${fnResult}`, error);
      }
    });

    // 호출 완료 후 대기 목록 초기화
    adCallbackRef.current = [];
  }

  // 2) ad - 광고 시청 후 콜백 등록
  useAdsListener('', '', { task: 'game-egg' }, checkAdsEgg);

  // react-unity-webgl의 isLoaded도 함께 확인 (더 안전한 방법)
  useEffect(() => {
    if (unityIsLoaded && loadingProgression === 1) {
      if (readyTimeoutRef.current) {
        clearTimeout(readyTimeoutRef.current);
      }

      readyTimeoutRef.current = setTimeout(() => {
        // console.log('Unity loaded event received'); // 디버깅용 로그
        setIsFullyReady(true);
      }, 1800);
    }
  }, [unityIsLoaded, loadingProgression]);

  // 대기 중인 호출들 처리
  useEffect(() => {
    processPendingCalls();
  }, [processPendingCalls]);

  // ##### 유니티 통신 JS 재정의 함수 등록 #####
  // Unity에서 호출할 함수 전역 등록
  useEffect(() => {
    window.SendToHtml = function (protocol, goName, fnResult) {
      // console.log('SendToHtml called:', protocol, goName, fnResult); // 디버깅용 로그

      if (protocol === 'mode' && mode !== null) {
        // console.log('Mode protocol, sending mode:', mode); // 디버깅용 로그
        fnSendMessage(goName, fnResult, mode);
      } else if (protocol === 'ads') {
        // console.log('Ads protocol triggered'); // 디버깅용 로그

        // 여기서 광고 시청 호출
        window.flutter_inappwebview?.callHandler('run_admob', '');
        // fnSendMessage(goName, fnResult, 1); // PC 에서 광고 시청 성공시 1을 보내도록 설정
        // 1) ad - 광고 시청 수행
        adCallbackRef.current.push({ goName, fnResult, arg: 1 });
        // 광고 성공시 sendMessage 호출
      } else if (protocol === 'quit') {
        // 게임 종료
        setMode(''); // 게임 화면 모드 초기화
        setShowInModal(false); // 모달 닫기
      }
    };

    window.ShowScreen = function () {
      //console.log('ShowScreen called:');
    };

    window.GetAccessToken = function () {
      //console.log('GetAccessToken called:');
      var accessToken = '';
      try {
        accessToken = window.localStorage.getItem('accessToken');
        if (accessToken === null || accessToken === undefined) {
          accessToken = '';
        } else {
          var obj = JSON.parse(accessToken);
          accessToken = obj.value;
        }
      } catch (ex) {
        //console.error(ex);
      }
      return accessToken;
    };

    window.ProcessQSMode = () => {};
    window.ProcessAds = () => {};
    window.ProcessQuitGame = () => {};

    return () => {
      // 컴포넌트 언마운트 시 정리
      if (readyTimeoutRef.current) {
        clearTimeout(readyTimeoutRef.current);
      }
    };
  }, [mode, fnSendMessage]);

  // mode 변경시 화면 초기 진입 화면 전환
  useEffect(() => {
    if (mode !== '' && isFullyReady) {
      fnSendMessage('WebInterfaceMgr', 'OnModeResult', mode); // 초기 화면
    } else {
      fnSendMessage('WebInterfaceMgr', 'OnModeResult', ''); // 초기 화면
    }
  }, [mode, fnSendMessage, isFullyReady]);

  // 로그인 이벤트 리스너 등록
  useEffect(() => {
    const unsubscribe = addLoginListener(handleLoginSuccess);
    return unsubscribe;
  }, [addLoginListener]);

  return (
    <UnityGameContext.Provider
      value={{
        ...unityContextValue,
        isFullyReady,
        // unityIsLoaded, // 굳이 필요 없음
        // loadingProgression, // 굳이 필요 없음
        // isUnityLoaded, // 굳이 필요 없음
        showInModal,
        setShowInModal,
        setGameMode, // 게임 모드 설정 함수
      }}
    >
      {/* 단일 Unity 인스턴스 */}
      <div
        style={{
          position: showInModal ? 'fixed' : 'absolute',
          top: showInModal ? '50%' : 'auto',
          left: showInModal ? '50%' : '-9999px',
          transform: showInModal ? 'translate(-50%, -50%)' : 'none',
          zIndex: showInModal ? 1000 : -1,
          backgroundColor: showInModal ? 'white' : 'transparent',
          padding: showInModal ? '20px' : '0',
          borderRadius: showInModal ? '8px' : '0',
          boxShadow: showInModal ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
          // transition: 'all 0.1s ease', // 딜레이 에니메이션
        }}
      >
        {unityProvider && (
          <Unity
            unityProvider={unityProvider}
            style={{
              width: '100vw',
              height: '100vh',
              display: 'block',
            }}
            devicePixelRatio={window.devicePixelRatio}
          />
        )}
      </div>

      {/* 모달 배경 */}
      {showInModal && (
        <div
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
          onClick={() => setShowInModal(false)}
        />
      )}
      {children}
    </UnityGameContext.Provider>
  );
};

// 로딩 상태를 가져오는 훅
export const useUnityGameStatus = () => {
  const context = useContext(UnityGameContext);
  if (!context) {
    throw new Error('useUnityGameStatus must be used within UnityGameProviderEgg');
  }
  return {
    isFullyReady: context.isFullyReady, // 게임 완전 준비 상태
    showInModal: context.showInModal, // 현재 게임 모달 상태
    setShowInModal: context.setShowInModal, // 게임 모달 상태 설정 함수
    setGameMode: context.setGameMode, // 게임 모드 설정 함수
  };
};
